import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Badge,
  useTheme,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Dashboard,
  Settings,
  Notifications,
  Add,
  AddCircle,
  PostAdd,
  TrendingUp,
  Flight,
  Hotel,
  CreditCard,
  LocalOffer,
  Map,
  Logout,
  Person,
  AdminPanelSettings,
} from '@mui/icons-material';
import { ThemeToggle } from './ThemeToggle';
import { NotificationCenter } from './NotificationCenter';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';

// Smooth glow animation
const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(56, 178, 172, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(56, 178, 172, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(56, 178, 172, 0.3);
  }
`;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(15, 23, 42, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  boxShadow: theme.palette.mode === 'light' 
    ? '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
    : '0 4px 20px rgba(0, 0, 0, 0.3)',
}));

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  color: active 
    ? (theme.palette.mode === 'light' ? '#1A365D' : '#ffffff')
    : (theme.palette.mode === 'light' ? 'rgba(26, 54, 93, 0.7)' : 'rgba(255, 255, 255, 0.7)'),
  fontWeight: active ? 600 : 500,
  textTransform: 'none',
  padding: '10px 20px',
  margin: '0 4px',
  borderRadius: 16,
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: 'unset',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(56, 178, 172, 0.08)' 
      : 'rgba(79, 209, 199, 0.08)',
    color: theme.palette.mode === 'light' ? '#1A365D' : '#ffffff',
    transform: 'translateY(-1px)',
  },
  '& .MuiButton-startIcon': {
    fontSize: '1.1rem',
    marginRight: '8px',
  },
  ...(active && {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(56, 178, 172, 0.12)' 
      : 'rgba(79, 209, 199, 0.12)',
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '24px',
      height: '3px',
      backgroundColor: theme.palette.mode === 'light' ? '#38B2AC' : '#4FD1C7',
      borderRadius: '2px 2px 0 0',
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(56, 178, 172, 0.16)' 
        : 'rgba(79, 209, 199, 0.16)',
    },
  }),
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  padding: theme.spacing(0.5, 1),
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(56, 178, 172, 0.08)' 
      : 'rgba(79, 209, 199, 0.08)',
    transform: 'scale(1.02)',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? 'rgba(26, 54, 93, 0.7)' : 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(56, 178, 172, 0.08)' 
      : 'rgba(79, 209, 199, 0.08)',
    color: theme.palette.mode === 'light' ? '#1A365D' : '#ffffff',
    transform: 'scale(1.05)',
  },
}));

const AddProgramButton = styled(Button)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? 'rgba(26, 54, 93, 0.7)' : 'rgba(255, 255, 255, 0.7)',
  fontWeight: 500,
  textTransform: 'none',
  padding: '10px 20px',
  margin: '0 4px',
  borderRadius: 16,
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: 'unset',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(56, 178, 172, 0.08)' 
      : 'rgba(79, 209, 199, 0.08)',
    color: theme.palette.mode === 'light' ? '#1A365D' : '#ffffff',
    transform: 'translateY(-1px)',
  },
  '& .MuiButton-startIcon': {
    fontSize: '1.1rem',
    marginRight: '8px',
  },
}));

export const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, signOut, signInWithGoogle } = useAuth();
  const admin = useAdmin();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const primaryItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Dashboard },
    { path: '/airlines', label: 'Airlines', icon: Flight },
    { path: '/hotels', label: 'Hotels', icon: Hotel },
    { path: '/cards', label: 'Cards', icon: CreditCard },
    { path: '/flight-map', label: 'Flight Map', icon: Map },
    { path: '/deals', label: 'Deals', icon: LocalOffer },
  ];

  const isActive = (path: string) => location.pathname === path;
  
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!user) {
      // If not logged in, trigger sign in
      handleSignIn();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleAddProgram = (type: 'airlines' | 'hotels' | 'cards') => {
    navigate(`/${type}`);
    handleAddMenuClose();
  };

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Failed to sign in:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      handleUserMenuClose();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  return (
    <StyledAppBar position="sticky" elevation={0}>
      <Toolbar sx={{ px: { xs: 2, md: 4 }, py: 1 }}>
        {/* Logo */}
        <Logo onClick={() => navigate('/dashboard')}>
          <Box sx={{ 
            background: theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)'
              : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <TrendingUp sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.mode === 'light' ? '#1A365D' : '#ffffff',
              fontFamily: '"SF Pro Display", "Inter", sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            PoinVoy
          </Typography>
          <Chip 
            label="Pro" 
            size="small" 
            sx={{ 
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)'
                : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
            }} 
          />
        </Logo>

        {/* Navigation Items */}
        <Box sx={{ display: 'flex', ml: 6, flex: 1 }}>
          {primaryItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavButton
                key={item.path}
                active={isActive(item.path)}
                startIcon={<Icon />}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </NavButton>
            );
          })}
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThemeToggle size="medium" variant="minimal" />
          
          <NotificationCenter />
          
          {/* Admin Panel Button - Only show for admins */}
          {admin.isAdmin && (
            <NavButton
              active={location.pathname.startsWith('/admin')}
              startIcon={<AdminPanelSettings />}
              onClick={() => navigate('/admin')}
              sx={{ 
                background: location.pathname.startsWith('/admin')
                  ? theme.palette.mode === 'light'
                    ? 'rgba(233, 30, 99, 0.08)'
                    : 'rgba(236, 72, 153, 0.08)'
                  : 'transparent',
                color: location.pathname.startsWith('/admin')
                  ? theme.palette.mode === 'light'
                    ? '#e91e63'
                    : '#ec4899'
                  : theme.palette.mode === 'light'
                    ? 'rgba(26, 54, 93, 0.7)'
                    : 'rgba(255, 255, 255, 0.7)',
              }}
            >
              Admin
            </NavButton>
          )}
          
          <StyledIconButton onClick={() => navigate('/settings')}>
            <Settings />
          </StyledIconButton>

          <AddProgramButton
            startIcon={<LocalOffer />}
            onClick={handleAddMenuOpen}
            title="Add Program"
          >
            Add Program
          </AddProgramButton>

          {/* User Avatar and Menu or Sign In Button */}
          {user ? (
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ ml: 1, p: 0 }}
            >
              <Avatar
                src={user?.photoURL}
                alt={user?.displayName || user?.email}
                sx={{
                  width: 40,
                  height: 40,
                  border: (theme) => `2px solid ${theme.palette.mode === 'light' ? '#38B2AC' : '#06b6d4'}`,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          ) : (
            <Button
              variant="contained"
              onClick={handleUserMenuOpen}
              sx={{
                ml: 1,
                background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: '0 4px 14px rgba(56, 178, 172, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #319795 0%, #38B2AC 100%)',
                  boxShadow: '0 6px 20px rgba(56, 178, 172, 0.4)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Sign In
            </Button>
          )}

          {/* User Menu - Only show when authenticated */}
          {user && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              onClick={handleUserMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                background: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(20px)',
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: theme.palette.mode === 'light' 
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(15, 23, 42, 0.95)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.displayName || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate('/settings')}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile & Settings</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
          )}

          {/* Add Program Menu */}
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={handleAddMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                background: theme.palette.mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.95)'
                  : 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(20px)',
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' 
                      ? 'rgba(56, 178, 172, 0.1)'
                      : 'rgba(79, 209, 199, 0.1)',
                  }
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 20,
                  width: 10,
                  height: 10,
                  bgcolor: theme.palette.mode === 'light' 
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(15, 23, 42, 0.95)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => handleAddProgram('airlines')}>
              <ListItemIcon>
                <Flight fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Airline Program"
                secondary="Add frequent flyer program"
              />
            </MenuItem>
            <MenuItem onClick={() => handleAddProgram('hotels')}>
              <ListItemIcon>
                <Hotel fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Hotel Program"
                secondary="Add hotel loyalty program"
              />
            </MenuItem>
            <MenuItem onClick={() => handleAddProgram('cards')}>
              <ListItemIcon>
                <CreditCard fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Credit Card"
                secondary="Add credit card for earning"
              />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};